import { Column } from "@/components/common/Table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { useEffect, useRef, useState } from "react";

export interface RecentVisit {
  id: number;
  date: string;
  type: string;
  facility: string;
  diagnosis: string;
}

const TruncatedText = ({
  value,
  maxWidth = "130px",
}: {
  value: string;
  maxWidth?: string;
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      // Check if the content is truncated
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [value]);

  const content = (
    <div
      ref={textRef}
      style={{ maxWidth }}
      className={`truncate whitespace-nowrap overflow-hidden text-ellipsis ${isTruncated ? "cursor-pointer" : ""
        }`}>
      {value}
    </div>
  );

  // Only wrap with tooltip if text is truncated
  if (!isTruncated) {
    return content;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent
          side='top'
          align='start'
          className='
            z-[9999]
             bg-gray-700 text-grey-100
            px-3 py-2 
            rounded-md 
            shadow-md
            text-xs 
            max-w-[260px]
            relative
          '>
          <Typography variant='regular-12' className='break-words'>
            {value}
          </Typography>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const columns: Column<RecentVisit>[] = [
  {
    key: "date",
    label: "Date",
    isSortable: false,
    cellClassName: "min-w-[150px]",
    headerClassName: "w-[150px]",
    render: (row) => row.date || "-",
  },
  {
    key: "type",
    label: "Type",
    isSortable: false,
    cellClassName: "min-w-[150px]",
    headerClassName: "w-[150px]",
    render: (row) => row.type || "-",
  },
  {
    key: "facility",
    label: "Facility",
    isSortable: false,
    cellClassName: "min-w-[150px]",
    headerClassName: "w-[150px]",
    render: (row) => {
      const value = row.facility || "-";
      return <TruncatedText value={value} maxWidth="130px" />;
    },
  },
  {
    key: "diagnosis",
    label: "Event Description",
    isSortable: false,
    cellClassName: "min-w-[343px]",
    headerClassName: "w-[343px]",
    render: (row) => {
      const value = row.diagnosis || "-";
      return <TruncatedText value={value} maxWidth='470px' />;
    },
  },
];
